import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FollowButton } from "../../components/buttons";
import EditPostModal from '../../components/modals/EditPostModal/EditPostModal'; // Import the modal
import { useAuth } from "../../contexts/AuthContext"; // Import useAuth hook
import supabase from '../../services/supabase';
import { fetchRoutes, fetchAccessibleRoutes, createRoute, updateRoute, deleteRoute, fetchRoutePoints } from '../../services/routeService';
import { fetchUserProfilePosts } from '../../services/postService';
import "./UserProfile.css";
import Routes from './Routes';
import { UserRoutesMap, UserProfileMap } from '../../components/map/MapDisplay';
import ProfileStats from './components/ProfileStats';
import ProfileSettings from './components/ProfileSettings';
import PostGrid from './components/PostGrid';

export default function UserProfile() {
  const { username } = useParams(); // Get username from URL
  const location = useLocation(); // Get location object
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true); // Keep local loading for profile data itself
  const [error, setError] = useState(null);
  const { user, loading: authLoading } = useAuth(); // Get user and AUTH loading state from context
  const [activeTab, setActiveTab] = useState(location.state?.initialTab || "posts");
  const [isOwnProfile, setIsOwnProfile] = useState(false); // Initialize as false
  const [sharingEnabled, setSharingEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [followingCount, setFollowingCount] = useState(0);
  const [posts, setPosts] = useState([]); // Added state for posts
  const navigate = useNavigate();
  const [statsPosition, setStatsPosition] = useState("bottom"); // Options: "bottom", "left", "picture-below"
  const [routes, setRoutes] = useState([]);
  const [showFullBio, setShowFullBio] = useState(false);
  const [routesAnimClass, setRoutesAnimClass] = useState('routes-anim-enter');
  const routesTabContentRef = useRef(null); // ref for routes-tab-content
  const [routesTabFullpage, setRoutesTabFullpage] = useState(false); // state for fullpage mode
  const [leaderboardPosition, setLeaderboardPosition] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);

  // Add state to track if profile has been loaded to prevent unnecessary refetching
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);

  // Routes state management (moved up from Routes component)
  const [isCreating, setIsCreating] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedRoutePoints, setSelectedRoutePoints] = useState([]);
  const [loadingSelectedRoute, setLoadingSelectedRoute] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Page visibility API to prevent unnecessary refetching on tab switch
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Reset profile loaded state when username changes (viewing different profile)
  useEffect(() => {
    setProfileLoaded(false);
  }, [username]);

  // Animation logic for fullscreen transition (must be top-level)
  useEffect(() => {
    if (activeTab === 'routes') {
      setRoutesAnimClass('routes-anim-enter');
      const timeout = setTimeout(() => {
        setRoutesAnimClass('routes-anim-enter-active');
      }, 10);
      return () => clearTimeout(timeout);
    }
  }, [activeTab]);

  // Prevent loading state when switching back to tab if data is already loaded
  useEffect(() => {
    if (isPageVisible && profileLoaded && profile && !loading) {
      // Data is already loaded, no need to show loading
      return;
    }
  }, [isPageVisible, profileLoaded, profile, loading]);

  // Handle fullscreen mode based on routes state
  useEffect(() => {
    const shouldBeFullscreen = isCreating || selectedRoute;
    setRoutesTabFullpage(shouldBeFullscreen);
    
    if (shouldBeFullscreen) {
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isCreating, selectedRoute]);

  // Fetch route points when a route is selected
  useEffect(() => {
    async function loadRoutePoints() {
      if (!selectedRoute) return;
      setLoadingSelectedRoute(true);
      const { data, error } = await supabase
        .from('route_points')
        .select('*')
        .eq('route_id', selectedRoute.id)
        .order('point_order', { ascending: true });
      if (error) {
        console.error('Failed to fetch route points:', error);
        setSelectedRoutePoints([]);
      } else {
        // Transform: add coordinates property for map logic
        const pointsWithCoordinates = (data || []).map(pt => ({
          ...pt,
          coordinates: (typeof pt.longitude === 'number' && typeof pt.latitude === 'number')
            ? [pt.longitude, pt.latitude]
            : undefined
        }));
        setSelectedRoutePoints(pointsWithCoordinates);
      }
      setLoadingSelectedRoute(false);
    }
    loadRoutePoints();
  }, [selectedRoute]);

useEffect(() => {
  // Don't refetch if profile is already loaded and page is not visible (tab switch)
  if (authLoading || (profileLoaded && !isPageVisible)) {
    return;
  }

  const getUserData = async () => {
    setLoading(true);
    try {
      console.log("[UserProfile Effect] Running with user from context:", user);

      if (!user) {
        console.log("[UserProfile Effect] No user found in context. App.js should redirect.");
        setError("Authentication required.");
        setLoading(false);
        return;
      }

      let profileData = null;
      let profileError = null;
      let ownProfile = false;
      let profileUserId = null;

      if (username) {
        // Fetch by username
        try {
          const profileResponse = await supabase
            .from("user_profiles")
            .select("*")
            .eq("username", username)
            .single();
          profileData = profileResponse?.data;
          profileError = profileResponse?.error;
          if (profileData) {
            profileUserId = profileData.user_id;
            ownProfile = user && profileData.user_id === user.id;
          }
        } catch (error) {
          console.error('[DEBUG] Error fetching profile by username:', error);
          profileError = error;
        }
      } else {
        // Fallback: own profile
        profileUserId = user.id;
        ownProfile = true;
        try {
          const profileResponse = await supabase
            .from("user_profiles")
            .select("*")
            .eq("user_id", profileUserId)
            .single();
          profileData = profileResponse?.data;
          profileError = profileResponse?.error;
        } catch (error) {
          console.error('[DEBUG] Error fetching own profile:', error);
          profileError = error;
        }
      }

      setIsOwnProfile(ownProfile);

      if (profileError) {
        if (profileError.code === "PGRST116" || profileError.code === "22P02") {
            setError("User not found");
            setLoading(false);
            return;
        }
        setError("Error loading profile");
        setLoading(false);
        return;
      }

      if (!profileData) {
          setError("User not found");
          setLoading(false);
          return;
      }

      // Use cached follower and following counts from user_profiles table
      setProfile(profileData);
      setFollowingCount(profileData.following_count || 0);
      setProfileLoaded(true); // Mark profile as loaded

      // Fetch leaderboard position for the profile user
      if (profileUserId) {
        try {
          const position = await getUserLeaderboardPosition(profileUserId);
          setLeaderboardPosition(position);
        } catch (leaderboardError) {
          console.error('[DEBUG] Error fetching leaderboard position:', leaderboardError);
          setLeaderboardPosition(null);
        }
      }

      // Fetch posts using the new privacy-aware function
      try {
        const userPosts = await fetchUserProfilePosts(profileUserId, user?.id);
        
        console.log('[DEBUG] User profile posts:', userPosts);
        
        // Filter and log posts with place data
        const postsWithPlaces = userPosts || [];
        const postsWithLocation = postsWithPlaces.filter(p => p.place && typeof p.place.lat === 'number' && typeof p.place.lng === 'number');
        
        console.log('[DEBUG] Total posts:', postsWithPlaces.length);
        console.log('[DEBUG] Posts with location data:', postsWithLocation.length);
        console.log('[DEBUG] Posts with location details:', postsWithLocation.map(p => ({
          id: p.id,
          title: p.title,
          place: p.place
        })));
        
        setPosts(postsWithPlaces);
      } catch (postsError) {
        console.error('[DEBUG] Error fetching user profile posts:', postsError);
        setPosts([]); // Set empty posts array on error
      }

    } catch (error) {
      console.error("[UserProfile Effect] Unexpected error:", error);
      setError("Error loading profile");
    } finally {
      setLoading(false);
    }
  };

  getUserData();
}, [user, authLoading, username]);



// Fetch routes when the component mounts
useEffect(() => {
  // Don't refetch routes if page is not visible (tab switch)
  if (!user || !isPageVisible) {
    return;
  }

  const loadRoutes = async () => {
    try {
      // Determine whose profile we're viewing
      const ownerUserId = profile?.user_id || user?.id;
      const viewerUserId = user?.id;
      console.log('[DEBUG] Loading routes (owner/viewer):', { ownerUserId, viewerUserId });

      const accessibleRoutes = await fetchAccessibleRoutes(ownerUserId, viewerUserId);
      console.log('[DEBUG] Fetched accessible routes:', accessibleRoutes);
      
      // Fetch points for each route
      const routesWithPoints = await Promise.all(
        (accessibleRoutes || []).map(async (route) => {
          try {
            console.log('[DEBUG] Fetching points for route:', route.id);
            const points = await fetchRoutePoints(route.id);
            console.log('[DEBUG] Fetched points for route', route.id, ':', points);
            
            // Attach coordinates array to each point if not present
            const pointsWithCoords = points.map(pt => ({
              ...pt,
              coordinates: (typeof pt.longitude === 'number' && typeof pt.latitude === 'number')
                ? [pt.longitude, pt.latitude]
                : undefined
            }));
            return { ...route, points: pointsWithCoords };
          } catch (pointError) {
            console.error('[DEBUG] Error fetching points for route', route.id, ':', pointError);
            // Return route without points if points fail to load
            return { ...route, points: [] };
          }
        })
      );
      console.log('[DEBUG] Final routes with points:', routesWithPoints);
      setRoutes(routesWithPoints);
    } catch (error) {
      console.error('[DEBUG] Error fetching routes:', error);
      // Set empty routes array instead of leaving it undefined
      setRoutes([]);
    }
  };
  
  loadRoutes();
}, [user, profile, isPageVisible]);



  // Cleanup body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleEditProfile = () => {
    navigate("/profile-setup");
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleFollowChange = (isFollowing) => {
    // Update the profile state immediately for a better UX
    if (profile) {
      setProfile({
        ...profile,
        follower_count: isFollowing 
          ? (profile.follower_count || 0) + 1 
          : Math.max((profile.follower_count || 0) - 1, 0)
      });
    }
  };


  




  // Add function to handle changing stats position
  const handleStatsPositionChange = (position) => {
    setStatsPosition(position);
  };

  // Function to parse tags from string to array
  const parseTagsFromString = (tagsString) => {
    if (!tagsString) return [];
    return tagsString.split(',').filter(tag => tag.trim() !== '');
  };



  // Function to handle viewing a route
  const handleViewRoute = (routeId) => {
    // Logic to view a route
    console.log('Viewing route:', routeId);
  };

  // Function to handle editing a route
  const handleEditRoute = (routeId) => {
    // Logic to edit a route
    console.log('Editing route:', routeId);
  };

  // Function to handle deleting a route
  const handleDeleteRoute = async (routeId) => {
    try {
      await deleteRoute(routeId);
      setRoutes(routes.filter(route => route.id !== routeId));
      console.log('Route deleted successfully:', routeId);
    } catch (error) {
      console.error('Error deleting route:', error);
    }
  };

  // Function to handle creating a new route
  const handleCreateRoute = async () => {
    try {
      const newRoute = await createRoute({
        name: 'New Route',
        description: 'Description of the new route',
        places: [] // Add logic to select places
      });
      setRoutes([...routes, newRoute]);
      console.log('Route created successfully:', newRoute);
    } catch (error) {
      console.error('Error creating route:', error);
    }
  };

  // Handler for profile updates from ProfileSettings component
  const handleProfileUpdate = (updatedFields) => {
    setProfile(prev => ({ ...prev, ...updatedFields }));
  };

  // Function to get user's all-time leaderboard position
  const getUserLeaderboardPosition = async (userId) => {
    if (!userId) return null;
    
    try {
      setLeaderboardLoading(true);
      
      // First check if user has any posts at all
      const { count: postCount, error: postCountError } = await supabase
        .from('new_post')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (postCountError) {
        console.error('Error checking user posts:', postCountError);
        return null;
      }

      // If user has no posts, they're unranked
      if (postCount === 0) {
        return null;
      }
      
      // Get all-time top posters (no date filter for all-time)
      const { data: topPosters, error } = await supabase
        .rpc('get_top_posters', {
          start_date: new Date(0).toISOString(), // Start from epoch (all-time)
          end_date: new Date().toISOString() // End at current date
        });

      if (error) {
        console.error('Error fetching leaderboard data:', error);
        return null;
      }

      if (!topPosters || topPosters.length === 0) {
        return null;
      }

      // Find the user's position in the leaderboard
      const userPosition = topPosters.findIndex(user => user.user_id === userId);
      
      if (userPosition === -1) {
        // User not in top 10
        return null;
      }

      return userPosition + 1; // Convert to 1-based position
    } catch (error) {
      console.error('Error getting leaderboard position:', error);
      return null;
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // Function to format leaderboard position display
  const formatLeaderboardPosition = (position) => {
    if (!position) return '📊 Unranked';
    
    const getOrdinalSuffix = (num) => {
      if (num >= 11 && num <= 13) return 'th';
      switch (num % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    const suffix = getOrdinalSuffix(position);
    
    // Add emoji for top 3 positions
    if (position === 1) return `🥇 ${position}${suffix}`;
    if (position === 2) return `🥈 ${position}${suffix}`;
    if (position === 3) return `🥉 ${position}${suffix}`;
    
    return `${position}${suffix}`;
  };

  // Use the local loading state for rendering the profile loading indicator
  // Don't show loading if we already have profile data and it's just a tab switch
  if (loading && (!profileLoaded || !profile)) {
    return <div className="profile-container loading" data-tab-switch={!isPageVisible}>Loading profile...</div>;
  }

  if (error) {
    return (
      <div className="profile-container">
        <h2>{error}</h2>
        {error === "User not found" && (
          <p>This profile doesn't exist or has been removed.</p>
        )}
        {isOwnProfile && error === "User not found" && (
          <button className="follow-btn" onClick={handleEditProfile}>
            Set Up Profile
          </button>
        )}
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container">
        <h2>User not found</h2>
        <p>This profile doesn't exist or has been removed.</p>
        {isOwnProfile && (
          <button className="follow-btn" onClick={handleEditProfile}>
            Set Up Profile
          </button>
        )}
      </div>
    );
  }



  return (
    <>
    <div className="profile-container">
      {/* Blue Header Section */}
      <div className="profile-header-wrapper">
        <div className={`profile-header profile-header-${statsPosition}`}> 
          {/* Profile Picture with Score */}
          <div className="profile-picture-container">
            {profile.profilePicture ? (
              <img 
                src={profile.profilePicture} 
                alt={`${profile.display_name}'s profile`} 
                className="profile-picture" 
                onError={(e) => {
                  console.error("Profile image failed to load:", profile.profilePicture);
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/150?text=Profile";
                }}
              />
            ) : (
              <div className="profile-picture-placeholder">
                {profile.username ? profile.username.charAt(0).toUpperCase() : "?"}
              </div>
            )}
            <div className="profile-score">
              {leaderboardLoading ? (
                'Loading...'
              ) : (
                formatLeaderboardPosition(leaderboardPosition)
              )}
            </div>
            {/* Render stats below picture if set to picture-below */}
            {statsPosition === "picture-below" && (
              <ProfileStats 
                profile={profile} 
                followingCount={followingCount} 
                statsPosition={statsPosition}
                postsCount={posts?.length || 0}
              />
            )}
          </div>
          {/* For 'bottom' position, render info and stats side by side */}
          {statsPosition === "bottom" ? (
            <div className="profile-header-row">
              <div className="profile-header-info">
                <h2 className="username">{profile.display_name || profile.username || "username"}</h2>
                <p className="user-handle">@{profile.username}</p>
                {profile.rank && (
                  <p className="rank-info">| Rank #{profile.rank} ({profile.rank_percentile || 0}% Top)</p>
                )}
                {/* Bio section, truncated with ellipsis and clickable */}
                {profile.bio && (
                  <div
                    className="profile-bio-truncated"
                    title="Click to view full bio"
                    onClick={() => setShowFullBio(true)}
                    tabIndex={0}
                    role="button"
                    style={{ cursor: 'pointer' }}
                  >
                    {profile.bio}
                  </div>
                )}
                {!isOwnProfile && user && (
                  <FollowButton 
                    userId={profile.user_id}
                    onFollowChange={handleFollowChange}
                  />
                )}
              </div>
              <ProfileStats 
                profile={profile} 
                followingCount={followingCount} 
                statsPosition={statsPosition}
                postsCount={posts?.length || 0}
              />
            </div>
          ) : (
            // For left or other positions, keep original rendering
            <>
              <div className="profile-header-info">
                <h2 className="username">{profile.display_name || profile.username || "username"}</h2>
                <p className="user-handle">@{profile.username}</p>
                {profile.rank && (
                  <p className="rank-info">| Rank #{profile.rank} ({profile.rank_percentile || 0}% Top)</p>
                )}
                {/* Bio section, truncated with ellipsis and clickable */}
                {profile.bio && (
                  <div
                    className="profile-bio-truncated"
                    title="Click to view full bio"
                    onClick={() => setShowFullBio(true)}
                    tabIndex={0}
                    role="button"
                    style={{ cursor: 'pointer' }}
                  >
                    {profile.bio}
                  </div>
                )}
                {!isOwnProfile && user && (
                  <FollowButton 
                    userId={profile.user_id}
                    onFollowChange={handleFollowChange}
                  />
                )}
              </div>
              {(statsPosition === "left") && <ProfileStats profile={profile} followingCount={followingCount} statsPosition={statsPosition} />}
            </>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="profile-tabs">
        <div 
          className={`profile-tab ${activeTab === "posts" ? "active" : ""}`}
          onClick={() => handleTabClick("posts")}
        >
          Posts
        </div>
        <div 
          className={`profile-tab ${activeTab === "routes" ? "active" : ""}`}
          onClick={() => handleTabClick("routes")}
        >
          Routes
        </div>
        <div 
          className={`profile-tab ${activeTab === "map" ? "active" : ""}`}
          onClick={() => handleTabClick("map")}
        >
          Map
        </div>
        {isOwnProfile && (
          <div 
            className={`profile-tab ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => handleTabClick("settings")}
          >
            Settings
          </div>
        )}
      </div>
      
      {/* Content based on active tab */}
      {activeTab === "settings" && isOwnProfile && (
        <ProfileSettings 
          profile={profile}
          sharingEnabled={sharingEnabled}
          setSharingEnabled={setSharingEnabled}
          notificationsEnabled={notificationsEnabled}
          setNotificationsEnabled={setNotificationsEnabled}
          onProfileUpdate={handleProfileUpdate}
        />
      )}
      
      {activeTab === "posts" && (
        <PostGrid 
          profile={profile}
          posts={posts}
          setPosts={setPosts}
          isOwnProfile={isOwnProfile}
                      />
      )}

      {/* Map tab - shows both routes and posts */}
      {activeTab === "map" && (
        <div style={{ padding: "2rem", textAlign: "center", color: "#777" }}>
          {/* Debug info */}
          {console.log('[DEBUG] MAP TAB - Tab clicked, rendering map')}
          {console.log('[DEBUG] MAP TAB - Routes data:', routes)}
          {console.log('[DEBUG] MAP TAB - Posts data:', posts)}
          {console.log('[DEBUG] MAP TAB - Filtered routes:', routes.filter(r => r.points && r.points.length > 0 && Array.isArray(r.points[0].coordinates)))}
          {console.log('[DEBUG] MAP TAB - Filtered posts:', posts.filter(p => p.place && typeof p.place.lat === 'number' && typeof p.place.lng === 'number'))}
          
          {/* Map with pins for routes and posts */}
          <UserProfileMap
            routesWithPoints={routes.filter(r => r.points && r.points.length > 0 && Array.isArray(r.points[0].coordinates))}
            posts={posts.filter(p => p.place && typeof p.place.lat === 'number' && typeof p.place.lng === 'number')}
            zoom={10}
            fallbackCenter={(() => {
              // Try to use user's homeLocation if it has lat/lng
              if (profile && profile.homeLocation && typeof profile.homeLocation.lat === 'number' && typeof profile.homeLocation.lng === 'number') {
                return [profile.homeLocation.lng, profile.homeLocation.lat];
              }
              return undefined;
            })()}
            onRouteClick={(route) => {
              console.log('Route clicked:', route);
              // Handle route click - could navigate to route detail or select route
            }}
            onPostClick={(post) => {
              console.log('Post clicked:', post);
              // Handle post click - navigate to post detail
              navigate(`/post/${post.id}`, { 
                state: { 
                  previousPath: location.pathname,
                  postData: post
                } 
              });
            }}
          />
        </div>
      )}

      {/* Full Bio Modal */}
      {showFullBio && (
        <div className="bio-modal-overlay" onClick={() => setShowFullBio(false)}>
          <div className="bio-modal" onClick={e => e.stopPropagation()}>
            <button className="bio-modal-close" onClick={() => setShowFullBio(false)}>&times;</button>
            <div className="bio-modal-content">{profile.bio}</div>
          </div>
        </div>
      )}

      {/* Routes Tab Content */}
      {activeTab === "routes" && !routesTabFullpage && (
        <div className="routes-tab-content" ref={routesTabContentRef}>
          <Routes 
            isOwnProfile={isOwnProfile} 
            userId={profile.user_id} 
            routes={routes} 
            isCreating={isCreating}
            setIsCreating={setIsCreating}
            selectedRoute={selectedRoute}
            setSelectedRoute={setSelectedRoute}
            selectedRoutePoints={selectedRoutePoints}
            setSelectedRoutePoints={setSelectedRoutePoints}
            loadingSelectedRoute={loadingSelectedRoute}
            setLoadingSelectedRoute={setLoadingSelectedRoute}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        </div>
      )}
    </div>
    
    {/* Fullscreen Routes Portal */}
    {activeTab === "routes" && routesTabFullpage && createPortal(
      <div className="routes-tab-content fullpage" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#fff',
        zIndex: 1000,
        overflow: 'hidden', /* Prevent scrolling - let the content handle it */
        boxShadow: '0 0 32px rgba(0,0,0,0.18)',
        animation: 'routesFullpageSlideIn 0.4s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <Routes 
          isOwnProfile={isOwnProfile} 
          userId={profile.user_id} 
          routes={routes} 
          isCreating={isCreating}
          setIsCreating={setIsCreating}
          selectedRoute={selectedRoute}
          setSelectedRoute={setSelectedRoute}
          selectedRoutePoints={selectedRoutePoints}
          setSelectedRoutePoints={setSelectedRoutePoints}
          loadingSelectedRoute={loadingSelectedRoute}
          setLoadingSelectedRoute={setLoadingSelectedRoute}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      </div>,
      document.body
    )}
    </>
  );
}
